<template>
  <div class="fc">
    <div class="fc__summary">
      <div class="fc__card">
        <span>{{ $t('crmForecastTotal') }}</span>
        <b>{{ showMoney ? (money(report.amount, true) || '—') : num(totalCount) }}</b>
      </div>
      <div v-if="showMoney" class="fc__card">
        <span>{{ $t('crmForecastWeighted') }}</span>
        <b class="fc__accent">{{ money(report.weighted, true) || '—' }}</b>
      </div>
      <div class="fc__card fc__card--warn">
        <span>{{ $t('crmForecastOverdue') }}</span>
        <b>{{ num(report.overdue_count) }}</b>
      </div>
      <div class="fc__chart">
        <div v-for="m in months" :key="m.key" class="fc__bar-wrap" :title="m.label">
          <div class="fc__bar">
            <i class="fc__bar-total" :style="{ height: pct(m.amount || m.count) + '%' }" />
            <i class="fc__bar-weighted" :style="{ height: pct(m.weighted || 0) + '%' }" />
          </div>
          <small>{{ m.short }}</small>
        </div>
      </div>
    </div>

    <div class="fc__lanes">
      <section v-for="m in lanes" :key="m.key" class="fc__lane" :class="{ 'fc__lane--late': m.key === 'late' }">
        <header class="fc__lane-head">
          <b>{{ m.label }}</b>
          <span class="fc__lane-count">{{ num(m.items.length) }}</span>
          <v-spacer />
          <small v-if="showMoney">{{ money(m.sum, true) }}</small>
        </header>
        <div class="fc__lane-body">
          <div v-for="d in m.items" :key="d.id" class="fc__deal" tabindex="0" @click="$emit('open', d)" @keydown.enter="$emit('open', d)">
            <div class="fc__deal-top">
              <b>{{ d.title }}</b>
              <span v-if="showMoney && d.amount !== null" class="fc__deal-amt">{{ money(d.amount, true) }}</span>
            </div>
            <div class="fc__deal-meta">
              <span>{{ fmtDate(d.expected_close_date) }}</span>
              <span class="fc__deal-prob"><i :style="{ width: (d.probability || 0) + '%' }" /></span>
              <span>{{ d.probability || 0 }}٪</span>
              <crm-avatar :user-id="d.owner_user_id" :size="18" />
            </div>
          </div>
          <div v-if="!m.items.length" class="fc__empty">—</div>
        </div>
      </section>
    </div>
    <div v-if="loading" class="fc__loading"><v-progress-linear indeterminate color="primary" /></div>
  </div>
</template>

<script>
import moment from 'jalali-moment'
import api from '../crmApi'
import { money, fmtDate, pick, isFa } from '../crmStore'
import { CrmAvatar } from '../components/CrmBits'

export default {
  name: 'CrmDealForecast',
  components: { CrmAvatar },
  props: { pipelineId: Number, params: { type: Object, default: () => ({}) }, showMoney: Boolean },
  data: () => ({ report: {}, deals: [], loading: false }),
  computed: {
    paramsKey () { return JSON.stringify([this.pipelineId, this.params]) },
    range () {
      const from = moment().startOf('month')
      return { from: from.clone().locale('en').format('YYYY-MM-DD'), to: from.clone().add(5, 'months').endOf('month').locale('en').format('YYYY-MM-DD') }
    },
    months () {
      const out = []
      const by = this.report.by_month || {}
      for (let i = 0; i < 6; i++) {
        const m = moment().startOf('month').add(i, 'months')
        const key = m.clone().locale('en').format('YYYY-MM')
        const fromDeals = this.deals.filter(d => (d.expected_close_date || '').slice(0, 7) === key)
        const row = by[key] || {
          count: fromDeals.length,
          amount: fromDeals.reduce((a, d) => a + (Number(d.amount) || 0), 0),
          weighted: fromDeals.reduce((a, d) => a + (Number(d.amount) || 0) * (Number(d.probability) || 0) / 100, 0)
        }
        out.push({
          key,
          label: isFa() ? m.clone().locale('fa').format('jMMMM jYYYY') : m.clone().locale('en').format('MMMM YYYY'),
          short: isFa() ? m.clone().locale('fa').format('jMMMM') : m.clone().locale('en').format('MMM'),
          ...row
        })
      }
      return out
    },
    maxVal () { return Math.max(1, ...this.months.map(m => Number(m.amount || m.count) || 0)) },
    totalCount () { return this.deals.length },
    lanes () {
      const today = moment().locale('en').format('YYYY-MM-DD')
      const late = this.deals.filter(d => d.expected_close_date && d.expected_close_date < today)
      const nodate = this.deals.filter(d => !d.expected_close_date)
      const lanes = [{ key: 'late', label: this.$t('crmForecastLate'), items: late }]
      this.months.forEach(m => lanes.push({ key: m.key, label: m.label, items: this.deals.filter(d => d.expected_close_date >= today && (d.expected_close_date || '').slice(0, 7) === m.key) }))
      lanes.push({ key: 'nodate', label: this.$t('crmForecastNoDate'), items: nodate })
      return lanes.map(l => ({ ...l, sum: l.items.reduce((a, d) => a + (Number(d.amount) || 0), 0) })).filter(l => l.items.length || /^\d/.test(l.key))
    }
  },
  watch: {
    paramsKey () { this.load() }
  },
  created () { this.load() },
  methods: {
    money,
    fmtDate,
    num (v) { return Number(v || 0).toLocaleString(isFa() ? 'fa-IR' : 'en-US') },
    pct (v) { return Math.round(((Number(v) || 0) / this.maxVal) * 100) },
    async load () {
      this.loading = true
      try {
        const [rep, deals] = await Promise.all([
          api.forecast({ pipeline_id: this.pipelineId, ...this.range }).catch(() => ({})),
          api.deals({ ...this.params, status: ['open'], itemsPerPage: 300, sortBy: ['expected_close_date'], options: { sortDesc: [false] } })
        ])
        this.report = rep || {}
        this.deals = pick(deals)
      } catch (e) {
        this.deals = []
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.fc { position: relative; display: flex; flex-direction: column; gap: 12px; height: 100%; min-height: 0; }
.fc__loading { position: absolute; top: 0; inset-inline: 0; }
.fc__summary { display: grid; grid-template-columns: repeat(3, minmax(0, 180px)) 1fr; gap: 8px; flex-shrink: 0; }
.fc__card { display: flex; flex-direction: column; justify-content: center; padding: 12px 14px; border-radius: 6px; background: #fff; border: 1px solid rgba(15, 23, 42, .08); }
.theme--dark .fc__card { background: #1e1e1e; border-color: rgba(255, 255, 255, .08); }
.fc__card span { font-size: .72rem; opacity: .6; }
.fc__card b { font-size: 1.2rem; font-weight: 800; }
.fc__accent { color: var(--v-primary-base); }
.fc__card--warn b { color: #c0392b; }
.fc__chart { display: flex; align-items: flex-end; gap: 10px; padding: 10px 14px; border-radius: 6px; background: #fff; border: 1px solid rgba(15, 23, 42, .08); min-height: 96px; }
.theme--dark .fc__chart { background: #1e1e1e; border-color: rgba(255, 255, 255, .08); }
.fc__bar-wrap { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 4px; }
.fc__bar-wrap small { font-size: .66rem; opacity: .6; white-space: nowrap; }
.fc__bar { position: relative; width: 100%; max-width: 40px; height: 64px; display: flex; align-items: flex-end; }
.fc__bar i { position: absolute; bottom: 0; inset-inline: 0; border-radius: 4px 4px 0 0; transition: height .3s; }
.fc__bar-total { background: rgba(25, 118, 210, .22); }
.fc__bar-weighted { background: var(--v-primary-base); inset-inline: 25% !important; }
.fc__lanes { flex: 1; min-height: 0; display: flex; gap: 10px; overflow-x: auto; overflow-y: hidden; padding-bottom: 6px; }
.fc__lane { flex: 0 0 250px; display: flex; flex-direction: column; min-height: 0; border-radius: 6px; background: rgba(15, 23, 42, .035); }
.theme--dark .fc__lane { background: rgba(255, 255, 255, .04); }
.fc__lane--late { background: rgba(192, 57, 43, .06); }
.fc__lane-head { display: flex; align-items: center; gap: 8px; padding: 10px 12px; font-size: .84rem; flex-shrink: 0; }
.fc__lane-head small { font-size: .74rem; font-weight: 700; opacity: .75; }
.fc__lane-count { font-size: .7rem; font-weight: 700; padding: 1px 7px; border-radius: 6px; background: rgba(127, 127, 127, .16); }
.fc__lane-body { flex: 1; overflow-y: auto; padding: 0 8px 10px; display: flex; flex-direction: column; gap: 6px; }
.fc__deal { flex-shrink: 0; background: #fff; border: 1px solid rgba(15, 23, 42, .08); border-radius: 6px; padding: 8px 10px; cursor: pointer; outline: none; }
.fc__deal:hover, .fc__deal:focus-visible { box-shadow: 0 3px 10px rgba(15, 23, 42, .08); }
.theme--dark .fc__deal { background: #262626; border-color: rgba(255, 255, 255, .08); }
.fc__deal-top { display: flex; gap: 6px; font-size: .8rem; }
.fc__deal-top b { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fc__deal-amt { font-weight: 800; white-space: nowrap; }
.fc__deal-meta { display: flex; align-items: center; gap: 6px; margin-top: 6px; font-size: .68rem; opacity: .75; }
.fc__deal-prob { flex: 1; height: 4px; border-radius: 4px; background: rgba(127, 127, 127, .15); overflow: hidden; }
.fc__deal-prob i { display: block; height: 100%; background: var(--v-primary-base); }
.fc__empty { text-align: center; opacity: .4; padding: 10px; }
@media (max-width: 1000px) {
  .fc__summary { grid-template-columns: repeat(3, 1fr); }
  .fc__chart { grid-column: 1 / -1; }
}
@media (max-width: 799px) {
  .fc__lane { flex-basis: 82vw; }
}
</style>
